import { PricedBond } from "../PricedBond";
import { getBondRatingIndex } from "../bondRating/getBondRatingIndex";
import { getBondRatingIndicesWithOffsetForMissingRating } from "../bondRating/getBondRatingIndicesWithOffsetForMissingRating";

const RATING_SPREAD_LIMIT = 4;
const MINIMAL_NUMBER_OF_SIMILAR_BONDS = 4;
const BONDS_BEFORE = 1;
const BONDS_AFTER = 3;

export type SubjectBondForSimilar = Pick<PricedBond, "ratings" | "bondIdCusip">;

export type PricedBondForSimilarBonds = Pick<PricedBond, "bondIdCusip" | "maturityDate" | "ratings">;

function filterBasedOnRatingWithSpread<T extends PricedBondForSimilarBonds>(
  pricedBonds: T[],
  refMoody: number,
  refSp: number,
  spread: number,
): T[] {
  const filtered = pricedBonds.filter((bondItem) => {
    const { moodysIndex, spIndex } = getBondRatingIndicesWithOffsetForMissingRating(bondItem);

    return (
      (moodysIndex >= refMoody - spread && moodysIndex <= refMoody + spread) ||
      (spIndex >= refSp - spread && spIndex <= refSp + spread)
    );
  });

  return filtered;
}

function filterBasedOnRating<T extends PricedBondForSimilarBonds>(
  pricedBonds: T[],
  refMoody: number,
  refSp: number,
): T[] {
  let result: T[] = [];
  for (let spread = 1; spread <= RATING_SPREAD_LIMIT; spread++) {
    result = filterBasedOnRatingWithSpread(pricedBonds, refMoody, refSp, spread);
    // +1 for the bond itself
    if (result.length >= MINIMAL_NUMBER_OF_SIMILAR_BONDS + 1) {
      break;
    }
  }

  return result;
}

function sliceFrameFromSortedResult<T extends PricedBondForSimilarBonds>(
  subjectBond: SubjectBondForSimilar,
  sortedResult: T[],
): T[] {
  const indexOfSubjectBond = sortedResult.findIndex((bond) => bond.bondIdCusip === subjectBond.bondIdCusip);

  if (indexOfSubjectBond === -1) {
    console.error("[filterSimilarBonds] indexOfSubjectBond not found, shouldn't happen");
    return [];
  }

  const frameIndices = [indexOfSubjectBond - BONDS_BEFORE, indexOfSubjectBond + BONDS_AFTER];

  if (frameIndices[0] < 0) {
    const underflow = 0 - frameIndices[0];
    frameIndices[1] = frameIndices[1] + underflow;
    frameIndices[0] = 0;
  }

  const lastIndex = sortedResult.length - 1;
  if (frameIndices[1] > lastIndex) {
    const overflow = frameIndices[1] - lastIndex;
    frameIndices[0] = frameIndices[0] - overflow;
    frameIndices[1] = lastIndex;
  }

  return sortedResult
    .slice(frameIndices[0], frameIndices[1] + 1) // +1 to make it inclusive
    .filter((bond) => bond.bondIdCusip !== subjectBond.bondIdCusip);
}

export function filterSimilarBonds<T extends PricedBondForSimilarBonds>(
  subjectBond: SubjectBondForSimilar,
  pricedBonds: T[],
): T[] {
  const { moodysIndex, spIndex } = getBondRatingIndex(subjectBond);

  const result = filterBasedOnRating(pricedBonds, moodysIndex, spIndex);

  const sortedResult = result.sort((a, b) => a.maturityDate.toMillis() - b.maturityDate.toMillis());

  if (sortedResult.length < MINIMAL_NUMBER_OF_SIMILAR_BONDS + 1) {
    return sortedResult.filter((bond) => bond.bondIdCusip !== subjectBond.bondIdCusip);
  }

  return sliceFrameFromSortedResult(subjectBond, sortedResult);
}
